import {
  AccessLevel,
  AccountData,
  Event,
  Visibility,
} from "@markit/common.types";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import {
  GetDateWithTime,
  GetTime,
  isEventRelatedType,
  isPaidTickets,
  isProductSpecificType,
  getThumbnailImageUrl,
} from "@markit/common.utils";
import { Colors } from "../../../utils/colors";
import ProfileEventTimelineSubItem from "./ProfileEventTimelineSubItem";
import EventOrganizerSubItem from "../EventItem/EventOrganizerSubItem";
import EventItemImage from "../EventItem/EventItemImage";
import { useMemo } from "react";
import { Icon } from "@iconify/react";
import { getEventDisplayedOrganizers } from "../../../utils/eventUtils/eventUtils";
import LocalSystemDateDisplay from "../../FullEvent/LocalSystemDateDisplay";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { FormatCurrency } from "../../../utils/FormatCurrency";

type ProfileEventTimelineCardItemProps = {
  event: Event;
  organizers: AccountData[];
  url: string;
  isMyAccount: boolean;
  isCreatorView?: boolean; // True if viewing the card items from creator mode manage
  isTimelineView?: boolean; /// true if the card is displayed within the timeline
};

// To display the event details on the profile page event timeline for the card view
const ProfileEventTimelineCardItem = (
  props: ProfileEventTimelineCardItemProps
) => {
  const { event, organizers, url, isMyAccount, isCreatorView, isTimelineView } =
    props;
  const { accountData } = useSelector(getAccountState).account;

  const styles = {
    title: {
      color: Colors.BLACK,
      fontSize: 16,
      fontWeight: "500",
    },
    description: {
      fontSize: 12,
      color: Colors.GRAY1,
    },
  };

  const thumbnailImage = useMemo(() => getThumbnailImageUrl(event), [event]);

  const isMyAccountSubscriber = useMemo(
    () =>
      isMyAccount &&
      !accountData.inCreatorMode &&
      accountData.phoneNumber !== "",
    [accountData.inCreatorMode, accountData.phoneNumber, isMyAccount]
  );

  const finalOrganizers = useMemo(() => {
    const foundOrganizers = event.cohosts.concat(event.createdBy);
    return getEventDisplayedOrganizers(
      event.createdBy,
      organizers.filter((organizer) => foundOrganizers.includes(organizer.uid))
    );
  }, [event.cohosts, event.createdBy, organizers]);

  /**
   * Show paid access barrier if these are true:
   * 1. Not viewing own account
   * 2. The access level is paid subscribers or checkout
   */
  const isPaidAccessLevel = useMemo(
    () =>
      !isMyAccount &&
      (event.accessLevel === AccessLevel.PAID_SUBSCRIBERS ||
        event.accessLevel === AccessLevel.CHECKOUT),
    [event.accessLevel, isMyAccount]
  );

  // Only show the hidden label if viewing the card items from creator mode manage and it's private visibility
  const renderHiddenLabel = useMemo(
    () =>
      isCreatorView && event.visibility === Visibility.Private ? (
        <div className="AlignedRow" style={{ gap: 4 }}>
          <Icon icon="ion:eye" height={14} style={{ color: Colors.ORANGE1 }} />
          <span style={{ fontSize: 12, color: Colors.ORANGE1 }}>Hidden</span>
        </div>
      ) : null,
    [event.visibility, isCreatorView]
  );

  return (
    <StandardBorderedContainer
      containerStyles={{
        backgroundColor: Colors.WHITE,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        border: `0.5px solid ${Colors.GRAY2}`,
      }}
    >
      <a
        className="AlignedRowSpaced"
        style={{
          flexGrow: 1,
          gap: 20,
          alignItems: "flex-start",
          padding: 10,
          borderRadius: 12,
          pointerEvents: isCreatorView ? "none" : "auto",
        }}
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        <div
          className="ColumnNormalSelect"
          style={{
            gap: 7,
            alignSelf: isEventRelatedType(event.eventType)
              ? undefined
              : "center",
          }}
        >
          <span className="TwoLineText" style={styles.title}>
            {event.title}
          </span>
          {isEventRelatedType(event.eventType) ? (
            <div className="ColumnNormalSelect" style={{ gap: 4 }}>
              <span style={styles.description}>
                <span>
                  {isTimelineView ? (
                    <span>{GetTime(event.start)} </span>
                  ) : (
                    GetDateWithTime(event.start, true)
                  )}
                </span>
                <LocalSystemDateDisplay
                  timestamp={event.start}
                  timezone={event.timezone}
                />
              </span>
              <span style={styles.description}>
                {event.isVirtual
                  ? "Virtual Location"
                  : event.privateLocation && !isMyAccount
                  ? "Hidden Location"
                  : event.googleDescription}
              </span>
              {finalOrganizers.length > 0 ? (
                <EventOrganizerSubItem organizers={finalOrganizers} />
              ) : null}
            </div>
          ) : null}
          {isProductSpecificType(event.eventType) ? (
            <div className="ColumnNormal" style={{ gap: 4 }}>
              {isMyAccountSubscriber || event.description ? (
                <span className="smallBodySubtext TwoLineText">
                  {isMyAccountSubscriber ? event.eventType : event.description}
                </span>
              ) : null}
              <div className="AlignedRow" style={{ gap: 7 }}>
                {renderHiddenLabel}
                {isMyAccountSubscriber ? (
                  <span className="smallBodySubtext">
                    Ordered: {GetDateWithTime(event.createdAt, true)}
                  </span>
                ) : (
                  <span className="smallBodySubtext">
                    {event.accessLevel === AccessLevel.CHECKOUT
                      ? (isPaidTickets(event)
                          ? FormatCurrency(event.customTickets[0].price / 100)
                          : "Checkout Only") + " • "
                      : event.accessLevel === AccessLevel.PAID_SUBSCRIBERS
                      ? "Paid Subscribers Only • "
                      : event.accessLevel === AccessLevel.SUBSCRIBERS
                      ? "Subscribers Only • "
                      : ""}{" "}
                    {event.eventType}
                  </span>
                )}
              </div>
              {isMyAccountSubscriber && finalOrganizers.length > 0 ? (
                <EventOrganizerSubItem organizers={finalOrganizers} />
              ) : null}
            </div>
          ) : (
            <div className="AlignedRow" style={{ gap: 7 }}>
              {renderHiddenLabel}
              <ProfileEventTimelineSubItem event={event} />
            </div>
          )}
        </div>
        {isPaidAccessLevel ? (
          <div style={{ position: "relative" }}>
            {thumbnailImage ? (
              <div
                className="Centering"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                }}
              >
                <Icon
                  icon="ion:ribbon"
                  height={40}
                  style={{ color: Colors.WHITE }}
                />
              </div>
            ) : null}
            <EventItemImage
              item={event}
              size={90}
              containerStyles={{
                filter: thumbnailImage
                  ? "brightness(80%) blur(3px)"
                  : undefined,
              }}
              altImageIcon="ion:ribbon"
              scaleImage
            />
          </div>
        ) : thumbnailImage || isProductSpecificType(event.eventType) ? (
          <EventItemImage item={event} size={90} scaleImage />
        ) : null}
      </a>
    </StandardBorderedContainer>
  );
};

export default ProfileEventTimelineCardItem;
